import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Download, FileSpreadsheet, Upload } from "lucide-react";
import { toast } from "sonner";
import MobileShell from "@/components/MobileShell";
import AdminDisplayPreference, { adminShellClassName, useAdminDisplayPreference } from "@/components/AdminDisplayPreference";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { analyseImportRows, createCsvTemplate, parseCsv } from "@/lib/bulkExerciseImport";
import AdminHomeButton from "@/components/AdminHomeButton";

const statusClass = {
  ready: "border-emerald-200 bg-emerald-50 text-emerald-700",
  conflict: "border-amber-200 bg-amber-50 text-amber-800",
  error: "border-rose-200 bg-rose-50 text-rose-700",
  created: "border-emerald-200 bg-emerald-50 text-emerald-700",
  updated: "border-sky-200 bg-sky-50 text-sky-700",
  skipped: "border-amber-200 bg-amber-50 text-amber-800",
};

export default function BulkExerciseImport() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [displayPreference, setDisplayPreference] = useAdminDisplayPreference();
  const [existingExercises, setExistingExercises] = useState([]);
  const [sourceRows, setSourceRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [pageError, setPageError] = useState("");
  const [overwriteRows, setOverwriteRows] = useState(new Set());
  const [results, setResults] = useState(new Map());
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    api.get("/admin/exercises")
      .then(({ data }) => setExistingExercises(data))
      .catch((error) => setPageError(formatApiError(error.response?.data?.detail) || "Unable to load existing exercises."));
  }, []);

  const analysedRows = useMemo(
    () => analyseImportRows(sourceRows, existingExercises),
    [sourceRows, existingExercises],
  );

  const rowState = (row) => {
    const result = results.get(row.rowNumber);
    if (result) return result.status;
    if (row.issues.some((issue) => issue.kind !== "existing")) return "error";
    if (row.existingTargetIds.length) return overwriteRows.has(row.rowNumber) ? "ready" : "conflict";
    return "ready";
  };

  const importableCount = analysedRows.filter((row) => rowState(row) === "ready").length;
  const conflictCount = analysedRows.filter((row) => rowState(row) === "conflict").length;
  const validationErrorCount = analysedRows.filter((row) => rowState(row) === "error").length;

  const chooseFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setPageError("");
    setFileName(file.name);
    setOverwriteRows(new Set());
    setResults(new Map());
    try {
      const parsed = parseCsv(await file.text());
      setSourceRows(parsed.rows);
    } catch (error) {
      setSourceRows([]);
      setPageError(error.message || "Unable to parse this CSV.");
    } finally {
      event.target.value = "";
    }
  };

  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob([createCsvTemplate()], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "exercise-bulk-import-template.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const toggleOverwrite = (rowNumber) => {
    setOverwriteRows((current) => {
      const next = new Set(current);
      if (next.has(rowNumber)) next.delete(rowNumber);
      else next.add(rowNumber);
      return next;
    });
    setResults(new Map());
  };

  const importRows = async () => {
    const overwriteCount = analysedRows.filter((row) => overwriteRows.has(row.rowNumber)).length;
    if (overwriteCount && !window.confirm(
      `Overwrite ${overwriteCount} existing exercise${overwriteCount === 1 ? "" : "s"}? This will replace the current exercise fields with the CSV values.`,
    )) return;

    setImporting(true);
    setPageError("");
    try {
      const payloadRows = analysedRows.map((row) => ({
        ...row.data,
        row_number: row.rowNumber,
        overwrite: overwriteRows.has(row.rowNumber),
      }));
      const { data } = await api.post("/admin/exercises/bulk-import", { rows: payloadRows });
      setResults(new Map(data.results.map((result) => [result.row_number, result])));
      const summary = data.summary;
      toast.success(`Import finished: ${summary.created} created, ${summary.updated} updated, ${summary.skipped} skipped, ${summary.error} errors.`);
      const refreshed = await api.get("/admin/exercises");
      setExistingExercises(refreshed.data);
    } catch (error) {
      setPageError(formatApiError(error.response?.data?.detail) || error.message || "Bulk import failed.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <MobileShell contentClassName={adminShellClassName(displayPreference)}>
      <header className="px-6 pt-8 pb-4" data-testid="bulk-exercise-import-page">
        <div className="flex items-start justify-between gap-3">
          <div>
            <button onClick={() => navigate("/admin/exercises")} className="mb-3 text-slate-500" aria-label="Back to Exercise Library">
              <ArrowLeft size={20} />
            </button>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600">Exercise library</p>
            <h1 className="mt-1 text-2xl font-bold font-display text-slate-900">Bulk Exercise Import</h1>
            <p className="mt-1 text-sm text-slate-500">Validate every CSV row before adding exercises and YouTube videos.</p>
          </div>
          <div className="flex flex-wrap gap-2"><AdminHomeButton /><AdminDisplayPreference value={displayPreference} onChange={setDisplayPreference} /></div>
        </div>
      </header>

      <section className="space-y-4 px-6 pb-8">
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-teal-50 text-teal-700"><Download size={18} /></span>
              <div className="flex-1">
                <h2 className="font-semibold text-slate-900">1. Download the template</h2>
                <p className="mt-1 text-sm text-slate-500">Keep the headers unchanged. You may leave optional metadata blank.</p>
                <Button className="mt-4" size="sm" variant="outline" onClick={downloadTemplate} data-testid="download-csv-template">
                  <Download size={16} /> Download CSV template
                </Button>
              </div>
            </div>
          </div>
          <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-sky-50 text-sky-700"><Upload size={18} /></span>
              <div className="flex-1">
                <h2 className="font-semibold text-slate-900">2. Upload and preview</h2>
                <p className="mt-1 text-sm text-slate-500">CSV files are parsed and checked in this browser before import.</p>
                <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={chooseFile} data-testid="bulk-import-file" />
                <Button className="mt-4" size="sm" onClick={() => inputRef.current?.click()}>
                  <FileSpreadsheet size={16} /> Choose CSV
                </Button>
                {fileName && <p className="mt-2 text-xs font-medium text-slate-600">{fileName}</p>}
              </div>
            </div>
          </div>
        </div>

        {pageError && (
          <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <AlertTriangle className="mt-0.5 shrink-0" size={18} /> {pageError}
          </div>
        )}

        {analysedRows.length > 0 && (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Summary label="Total rows" value={analysedRows.length} />
              <Summary label="Ready" value={importableCount} tone="text-emerald-700" />
              <Summary label="Existing conflicts" value={conflictCount} tone="text-amber-700" />
              <Summary label="Validation errors" value={validationErrorCount} tone="text-rose-700" />
            </div>

            <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm">
              <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-semibold text-slate-900">CSV preview</h2>
                  <p className="mt-1 text-xs text-slate-500">Exact names, URLs, and IDs are checked. Similar exercise names remain separate.</p>
                </div>
                <Button onClick={importRows} disabled={importing || importableCount === 0} data-testid="import-valid-rows">
                  <Upload size={16} /> {importing ? "Importing..." : `Import valid rows (${importableCount})`}
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[980px] text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Row</th>
                      <th className="px-4 py-3">Exercise</th>
                      <th className="px-4 py-3">YouTube</th>
                      <th className="px-4 py-3">Metadata</th>
                      <th className="px-4 py-3">Validation</th>
                      <th className="px-4 py-3">Status / action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {analysedRows.map((row) => {
                      const state = rowState(row);
                      const result = results.get(row.rowNumber);
                      const canOverwrite = row.existingTargetIds.length === 1
                        && row.issues.length > 0
                        && row.issues.every((issue) => issue.kind === "existing");
                      return (
                        <tr key={row.rowNumber} className={state === "error" ? "bg-rose-50/40" : state === "conflict" ? "bg-amber-50/40" : ""}>
                          <td className="px-4 py-4 font-mono text-xs text-slate-500">{row.rowNumber}</td>
                          <td className="px-4 py-4 align-top">
                            <div className="font-semibold text-slate-900">{row.data.exercise_name || "—"}</div>
                            <div className="mt-1 text-xs text-slate-500">{row.data.condition || "Default condition"}</div>
                          </td>
                          <td className="max-w-[260px] px-4 py-4 align-top">
                            <div className="break-all text-xs text-slate-600">{row.data.video_url || "No video"}</div>
                            {row.data.youtube_video_id && <div className="mt-1 font-mono text-xs text-slate-500">{row.data.youtube_video_id}</div>}
                          </td>
                          <td className="px-4 py-4 align-top text-xs text-slate-600">
                            <div>{[row.data.phase, row.data.level].filter(Boolean).join(" · ") || "—"}</div>
                            <div className="mt-1">{row.data.sets || "—"} sets · {row.data.reps || "—"} reps · {row.data.hold || "—"} hold</div>
                            {row.data.playlist && <div className="mt-1 font-medium text-teal-700">Playlist: {row.data.playlist}</div>}
                          </td>
                          <td className="px-4 py-4 align-top">
                            {row.issues.length ? (
                              <ul className="space-y-1 text-xs text-slate-700">
                                {row.issues.map((issue, index) => <li key={`${issue.message}-${index}`}>• {issue.message}</li>)}
                              </ul>
                            ) : <span className="text-xs text-emerald-700">No conflicts</span>}
                            {result?.message && <p className="mt-2 text-xs font-medium text-slate-700">{result.message}</p>}
                          </td>
                          <td className="px-4 py-4 align-top">
                            <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClass[state] || statusClass.error}`}>
                              {state}
                            </span>
                            {canOverwrite && !result && (
                              <label className="mt-3 flex max-w-[210px] cursor-pointer items-start gap-2 text-xs font-medium text-amber-900">
                                <input
                                  type="checkbox"
                                  className="mt-0.5 h-4 w-4 accent-amber-600"
                                  checked={overwriteRows.has(row.rowNumber)}
                                  onChange={() => toggleOverwrite(row.rowNumber)}
                                />
                                I confirm overwrite of the matched exercise
                              </label>
                            )}
                            {row.existingTargetIds.length > 1 && (
                              <p className="mt-2 max-w-[210px] text-xs text-rose-700">Overwrite unavailable because fields match different exercises.</p>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </section>
    </MobileShell>
  );
}

function Summary({ label, value, tone = "text-slate-900" }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
      <div className={`text-2xl font-bold ${tone}`}>{value}</div>
      <div className="mt-1 text-xs font-medium text-slate-500">{label}</div>
    </div>
  );
}
